import { runInnerFunction } from "../../compatManager";
import { PIPFormClient } from "./pipformclient";

export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function generatePropertyPages(
    pdf: any,
    propertyTables: NodeListOf<Element>,
    options: PIPFormClient.PdfGenerationConfig
): Promise<void> {
    const { contentWidth, contentHeight, xOffset } = options;
    
    for (let propertyIndex = 3; propertyIndex < propertyTables.length-2; propertyIndex += 2) {
        pdf.addPage();
        for (let pairElementIndex = 0; pairElementIndex < 2; pairElementIndex++) {
            if (propertyIndex+pairElementIndex < propertyTables.length-2) {
                const propertyCanvas = await (window as any).html2canvas(propertyTables[propertyIndex + pairElementIndex], {
                    scale: 4,
                    height: (contentHeight / 2) + 10,
                    width: contentWidth * 1.4,
                    windowWidth: contentWidth,
                    windowHeight: (contentHeight / 2) + 10,
                    useCORS: true,
                    allowTaint: true,
                });
                pdf.addImage(propertyCanvas.toDataURL('image/jpeg', 1.0), 
                'JPEG', xOffset+13, pairElementIndex === 0 ? 30 : -35 + (pairElementIndex * (contentHeight / 2)), contentWidth + 30, contentHeight / 2 - 100);
            }
        }
    }
}

async function generateTransfereePages(
    pdf: any,
    transfereeSection: Element,
    transfereeTables: NodeListOf<Element>,
    options: PIPFormClient.PdfGenerationConfig,
    isPropertyCountEven: boolean
): Promise<void> {
    let displacement = 0;
    const { contentWidth, contentHeight, xOffset } = options;
    const isCorporation = transfereeSection.textContent?.includes('Corporation # 1');
    const baseCanvasConfig = { scale: 4, width: contentWidth * 1.4, windowWidth: contentWidth, useCORS: true, allowTaint: true };

    // odd number of properties will always occupy till the end of a page. 
    // In such cases, the first transferee will always start from a new page.
    if (!isPropertyCountEven) {
        pdf.addPage();
    }
    const headerHeight = (contentHeight * 0.60) + (isCorporation ? 60 : 40);
    const transfereeCanvas = await (window as any).html2canvas(transfereeSection, { ...baseCanvasConfig, height: headerHeight, windowHeight: headerHeight });
    const headerY = isPropertyCountEven ? (contentHeight/2 - 50): (isCorporation ? 30 : 20);

    pdf.addImage(transfereeCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', xOffset + 5, headerY, contentWidth + 30, (contentHeight * 0.60) - 125);
    pdf.setLineWidth(0.5);
    pdf.line(xOffset + 6, headerY + (contentHeight * 0.60) - 125, xOffset + contentWidth + 26.5, headerY + (contentHeight * 0.60) - 125);

    // In case of even number of properties, the first transferee will always follow 
    // the last property on that same page. So if there are more than one transferee, those would start from a new page
    if (isPropertyCountEven) {
        pdf.addPage();
        displacement = 30;
    }
    if (transfereeTables.length > 1) {
        const firstTransfereeCanvas = await (window as any).html2canvas(transfereeTables[1], {
            ...baseCanvasConfig,
            height: (contentHeight * 0.40) + 170,
            windowHeight: (contentHeight * 0.40) + 170,
        });
        pdf.addImage(firstTransfereeCanvas.toDataURL('image/jpeg', 1.0), 
        'JPEG', xOffset + 13, isPropertyCountEven ? displacement : (contentHeight * 0.60) - 80, contentWidth + 30, contentHeight * 0.40);
    }
    
    // Handle remaining transferees (two per page)
    const remainingTransfereeConfig = {...baseCanvasConfig, height: (contentHeight / 2) + 85, windowHeight: (contentHeight / 2) + 85,};

    for (let index = 2; index < transfereeTables.length - 1; index += 2) {
        if (!isPropertyCountEven) {
            pdf.addPage();
        }
        const transfereeCanvas1 = await (window as any).html2canvas(transfereeTables[index], remainingTransfereeConfig);
        pdf.addImage(transfereeCanvas1.toDataURL('image/jpeg', 1.0), 
        'JPEG', xOffset + 13, isPropertyCountEven ? contentHeight/2 : 20, contentWidth + 30, (contentHeight / 2) - 55);
        
        if (isPropertyCountEven) {
            pdf.addPage();
        }

        // Second transferee if available and valid
        const hasValidSecondTransferee = index + 1 < transfereeTables.length - 1 || 
        (index + 1 > 2 && index + 1 === transfereeTables.length - 1 && (index + 1) % 2 !== 0);

        if (hasValidSecondTransferee) {
            const transfereeCanvas2 = await (window as any).html2canvas(transfereeTables[index + 1], remainingTransfereeConfig);
            pdf.addImage(transfereeCanvas2.toDataURL('image/jpeg', 1.0), 
            'JPEG', xOffset + 13, isPropertyCountEven ? 20 : (contentHeight / 2) - 15, contentWidth + 30, (contentHeight / 2) - 55);
        }
    }
}

async function generateFinalPage(
    pdf: any,
    transfereeTables: NodeListOf<Element>, 
    certificationSection: Element, 
    options: PIPFormClient.PdfGenerationConfig, 
    isPropertyCountEven: boolean
): Promise<void> {
    const { contentWidth, contentHeight, xOffset } = options;
    const transfereeCount = transfereeTables.length;
    const hasOddTransferees = transfereeCount % 2 !== 0;
    
    // Odd number of properties means the transferees will start from a new page, 
    // and in any of such cases, if the number of transferees are more than 1, 
    // both the last transferee and the certification section won't fit on a single page.
    if (!isPropertyCountEven && transfereeCount !== 1) {
        pdf.addPage();
    }

    // we manually add the first two transferees in the generateTransfereePages function, 
    // in such a case we don't need to explicitly add the final transferee again. 
    // Also if the number of transferees is more than 2 and EVEN, those would be added by 
    // the loop defined in the generateTransfereePages function.
    if (transfereeCount > 2 && hasOddTransferees) {
        const lastTransfereeCanvas = await (window as any).html2canvas(
            transfereeTables[transfereeCount - 1], 
            {
                scale: 4,
                height: (contentHeight / 2) + 85,
                width: contentWidth * 1.4,
                windowWidth: contentWidth,
                windowHeight: (contentHeight / 2) + 85,
                useCORS: true,
                allowTaint: true,
            });
        const displacement = isPropertyCountEven ? (hasOddTransferees ? contentHeight/2 : 30) : 50;
        pdf.addImage(lastTransfereeCanvas.toDataURL('image/jpeg', 1.0), 
        'JPEG', xOffset + 13, displacement, contentWidth + 30, (contentHeight / 2) - 55);
    }

    // In case of even number of properties, the first trasnferee always follows the last 
    // property on that same page. If there were even number of transferees, the certification 
    // section would be on the same page as the last transferee. In all the other cases, 
    // a new page is needed for the certification section.
    if (isPropertyCountEven && transfereeCount > 1 && hasOddTransferees) {
        pdf.addPage();
    }

    const certificationCanvas = await (window as any).html2canvas(certificationSection, {
        scale: 4,
        height: contentHeight / 2,
        width: contentWidth * 1.4,
        windowWidth: contentWidth,
        windowHeight: contentHeight / 2,
        useCORS: true,
        allowTaint: true,
    });

    const certificationDisplacement = isPropertyCountEven
        ? (transfereeCount % 2 === 0 ? contentHeight/2 : 30)
        : (transfereeCount >= 2 && transfereeCount % 2 === 0 ? 20 : contentHeight / 2);

    pdf.addImage(certificationCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', xOffset + 5, certificationDisplacement, contentWidth + 30, (contentHeight / 2) - 100);
}

export async function generatePdfPIPS(
    content: HTMLElement, 
    options: PIPFormClient.PdfGenerationConfig,
    filename: string
): Promise<void> {
    const { contentWidth, contentHeight, xOffset } = options;
    const pdf = new (window as any).jspdf.jsPDF('p', 'pt', 'a4');
    const findElementByText = (selector: string, text: string) => {
        const elements = content.querySelectorAll(selector);
        return Array.from(elements).find(element => 
            element.textContent?.trim() === text
        );
    };
    const propertyTables = content.querySelectorAll('table[style*="border:1px solid gray"]');
    const transfereeSection = findElementByText('td.mof-section-title', 'Transferee Information')?.closest('table');
    const certificationSection = findElementByText('td.mof-section-title', 'Certification')?.closest('table');
    
    if (!transfereeSection || !certificationSection) {
        throw new Error('Required sections not found in the document');
    }

    const canvas = await (window as any).html2canvas(content, {
        scale: 4,
        height: contentHeight,
        width: contentWidth * 1.4,
        windowWidth: contentWidth,
        windowHeight: contentHeight,
        useCORS: true,
        allowTaint: true,
    });
    pdf.addImage(canvas.toDataURL('image/jpeg', 1.0), 'JPEG', xOffset, 15, contentWidth + 30, contentHeight - 200);
    pdf.setLineWidth(0.5);
    pdf.line(xOffset + 7, contentHeight - 185, xOffset + contentWidth + 26.5, contentHeight - 185);
    await generatePropertyPages(pdf, propertyTables, options);
    const transfereeTables = content.querySelectorAll('table[style*="border:2px solid gray"]');
    await generateTransfereePages(pdf, transfereeSection, transfereeTables, options, propertyTables.length % 2 === 0);
    await generateFinalPage(pdf, transfereeTables, certificationSection, options, propertyTables.length % 2 === 0);
    pdf.save(filename);
}

export async function CreatePIPS(sid: string, TPAppXML: ajaxslt.XNode) {
    var item: ajaxslt.XNode = TPAppXML;
    var node: ajaxslt.XNode = item;
    var info: PIPFormClient.PIPInfo = PIPFormClient.ConvertOldTPAppXML(node);
    var lawyernode: ajaxslt.XNode = node.selectSingleNode('lawyer');
    var tpanode: ajaxslt.XNode = lawyernode ? lawyernode.selectSingleNode('ThirdPartyAcounts') : null;
    var tenode: ajaxslt.XNode = tpanode ? tpanode.selectSingleNode("Account[@Party='TeranetExpress']") : null;
    var tnode: ajaxslt.XNode = tpanode ? tpanode.selectSingleNode("Account[@Party='Teraview']") : null;
    var account: string = null;
    var password: string = null;

    if (tnode && tnode.getAttribute('Account') != null && tnode.getAttribute('Account') > '') {
        account = tnode.getAttribute('Account');
    }

    if (tenode && tenode.getAttribute('TVNumber') != null && tenode.getAttribute('TVNumber') > '') {
        password = tenode.getAttribute('TVNumber');
    }
    // validation
    var flagValidate: boolean = ValidatePIPS(info);
    if (!flagValidate) {
        return;
    }

    // server Call
    var missingFields = [];
    if (account == null || account == "")
        missingFields.push("Teraview Account");
    if (password == null || password == "")
        missingFields.push("TV Number");
    if (missingFields.length > 0) {
        var alertMSG = "Please enter the following required fields: \n";
        for (var _i = 0, missingFields_1 = missingFields; _i < missingFields_1.length; _i++) {
            var field = missingFields_1[_i];
            alertMSG += field + "\n";
        }
        alert(alertMSG);
        return;
    }
    var result = await PIPFormClient.CreateNewForm(info);
    if (result.formId > '') {
        node.setAttribute('PIPFormID', result.formId);
        runInnerFunction('SetPageDirty', null);
        return result.formId;
    }
    else if (result.error > '') {
        // The MOF server rejected the save — show its actual reason (e.g. invalid email)
        alert('The PIPS form could not be created. Teraview reported:\n\n' + result.error);
    }
    else {
        alert('Form creation failed.\nPlease confirm that your file contains valid PIN(s), Transferee name(s), \nand birth date(s), then try again.');
    }
}

export function ValidatePIPS(info) {
    console.log("info", info);
    if (info.Purchasers.length == 0) {
        alert("At least one Purchaser is required.");
        return false;
    } else {
        for (var j = 0; j < info.Purchasers.length; j++) {
            if (info.Purchasers[j].EntityType == "1") {
                // Corporation Name 
                if (info.Purchasers[j].Name == "" || info.Purchasers[j].Name == null) {
                    alert("Name of Corporation is required");
                    return false;
                }
            }
            else {
                // First Name 
                if (info.Purchasers[j].FirstName == "" || info.Purchasers[j].FirstName == null) {
                    alert("First Name of Purchaser is required");
                    return false;
                }

                // Last Name 
                if (info.Purchasers[j].LastName == "" || info.Purchasers[j].LastName == null) {
                    alert("Last Name of Purchaser is required");
                    return false;
                }

                // Birth Date 
                if (info.Purchasers[j].BirthDate == "" || info.Purchasers[j].BirthDate == null) {
                    alert("Birth Date of Purchaser " + info.Purchasers[j].FirstName + " " + info.Purchasers[j].LastName + " is required");
                    return false;
                } else {
                    if (info.Purchasers[j].BirthDate.indexOf("-") > -1) {
                        // format birth date to yyyy/mm/dd
                        var birthDateArr = info.Purchasers[j].BirthDate.split("-");
                        var todayTime = new Date(birthDateArr[1] + "/" + birthDateArr[2] + "/" + birthDateArr[0]);
                        console.log("todayTime", todayTime.getFullYear() + '/' + ('0' + (todayTime.getMonth() + 1)).slice(-2) + '/' + ('0' + todayTime.getDate()+1).slice(-2));
                        info.Purchasers[j].BirthDate = todayTime.getFullYear() + '/' + ('0' + (todayTime.getMonth() + 1)).slice(-2) + '/' + ('0' + todayTime.getDate()+1).slice(-2);
                    }
                }
            }
        }
    }

    // validate Properties
    if (info.Properties.length == 0) {
        alert("At least one Property is required.");
        return false;
    } else {
        // validate PIN
        for (var i = 0; i < info.Properties.length; i++) {
            if (info.Properties[i].PINBlock == "" || info.Properties[i].PINBlock == null) {
                alert("PINBlock is required in Property");
                return false;
            }

            if (info.Properties[i].PINProperty == "" || info.Properties[i].PINProperty == null) {
                alert("PINProperty is required in Property");
                return false;
            }
        }
    }

    // validate Lawyer email — the MOF acknowledgement section requires a valid email.
    // A missing or malformed value (e.g. a firm name pasted into the email field) is
    // rejected server-side with no visible error, so catch it here with a clear message.
    var email = (info.Lawyer && info.Lawyer.Email ? info.Lawyer.Email : "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        var shownEmail = email ? '"' + email + '"' : "(empty)";
        alert("The firm email " + shownEmail + " is not a valid email address.\n" +
            "Please correct the firm or staff email in your settings, then create the form again.");
        return false;
    }
    return true;
}

// The MOF form page is written for jQuery 1.x but is served with jQuery 3.5.0, where
// $(...).load(handler) was removed — its document-ready handler throws
// "e.indexOf is not a function" and the page's own submit/validation wiring dies.
// Restore the jQuery 1.x .load(handler) signature inside the iframe before the page's
// ready handler fires, and re-arm on every navigation (the form POSTs natively).
export function patchPipsIframeJQuery(iframe: HTMLIFrameElement): void {
    const tryPatch = (): boolean => {
        try {
            const w = iframe.contentWindow as any;
            if (!w) return false;
            if (!w.lddPipsPagehideHooked) {
                w.lddPipsPagehideHooked = true;
                w.addEventListener('pagehide', () => startFastPoll());
            }
            if (w.jQuery && w.jQuery.fn && !w.lddPipsJQueryLoadPatched) {
                const origLoad = w.jQuery.fn.load;
                w.jQuery.fn.load = function (this: any, a: any, ...rest: any[]) {
                    if (typeof a === 'function') {
                        return this.on('load', a);
                    }
                    return origLoad.apply(this, [a, ...rest]);
                };
                w.lddPipsJQueryLoadPatched = true;
            }
            return !!w.lddPipsJQueryLoadPatched;
        } catch (e) {
            return false;
        }
    };
    const startFastPoll = () => {
        const start = Date.now();
        const timer = setInterval(() => {
            // poll fast so the patch lands between jQuery loading and document ready
            if (tryPatch() || Date.now() - start > 20000) {
                clearInterval(timer);
            }
        }, 10);
    };
    startFastPoll();
}

// whenever the form is transformed (add Property, add purchaser, etc)
export function removeHeaderFooter(iframe: HTMLIFrameElement, showiFrame: boolean) {
    try{
        iframe.contentWindow.document.querySelector('nav').style.display = 'none';
        iframe.contentWindow.document.querySelector('nav').style.overflowX = 'hidden';
        iframe.contentWindow.document.querySelector('header').style.display = 'none';
        iframe.contentWindow.document.getElementById('footer').style.display = 'none';
        if(showiFrame){
            iframe.style.height = iframe.contentWindow.document.body.scrollHeight+20 + 'px';
        }
    } catch (e){}
}